import React, { useEffect } from "react";
import { Analytics } from "@vercel/analytics/react";
import "@/App.css";
import "@/index.css";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { Toaster } from "sonner";
import { HelmetProvider } from "react-helmet-async";
import { I18nProvider } from "./i18n/I18nProvider";
import { AuthProvider } from "./auth/AuthProvider";
import ProtectedRoute from "./auth/ProtectedRoute";
import Nav from "./components/Nav";
import Footer from "./components/Footer";
import CustomCursor from "./components/CustomCursor";

import Home from "./pages/Home";
import ServicesPage from "./pages/ServicesPage";
import ServiceDetailPage from "./pages/ServiceDetailPage";
import PortfolioPage from "./pages/PortfolioPage";
import StudioPage from "./pages/StudioPage";
import PricingPage from "./pages/PricingPage";
import BlogPage from "./pages/BlogPage";
import BlogPostPage from "./pages/BlogPostPage";
import ContactPage from "./pages/ContactPage";
import AdminLoginPage from "./pages/AdminLoginPage";
import AdminLeadsPage from "./pages/AdminLeadsPage";
import AdminBlogPage from "./pages/AdminBlogPage";
import AdminGeneratorPage from "./pages/AdminGeneratorPage";

function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => { window.scrollTo({ top: 0, behavior: "instant" }); }, [pathname]);
  return null;
}

function PublicLayout({ children }) {
  return (
    <>
      <Nav />
      {children}
      <Footer />
    </>
  );
}

function AppShell() {
  const { pathname } = useLocation();
  const isAdminRoute = pathname.startsWith("/admin");

  return (
    <div className="grain min-h-screen bg-[#050505] text-ink-100 font-sans-display antialiased">
      {!isAdminRoute && <CustomCursor />}
      <Routes>
        <Route path="/admin/login" element={<AdminLoginPage />} />
        <Route path="/admin/leads" element={<ProtectedRoute><AdminLeadsPage /></ProtectedRoute>} />
        <Route path="/admin/blog" element={<ProtectedRoute><AdminBlogPage /></ProtectedRoute>} />
        <Route path="/admin/studio" element={<ProtectedRoute><AdminGeneratorPage /></ProtectedRoute>} />
        <Route path="/admin" element={<ProtectedRoute><AdminLeadsPage /></ProtectedRoute>} />

        <Route path="/" element={<PublicLayout><Home /></PublicLayout>} />
        <Route path="/services" element={<PublicLayout><ServicesPage /></PublicLayout>} />
        <Route path="/services/:slug" element={<PublicLayout><ServiceDetailPage /></PublicLayout>} />
        <Route path="/portfolio" element={<PublicLayout><PortfolioPage /></PublicLayout>} />
        <Route path="/studio" element={<PublicLayout><StudioPage /></PublicLayout>} />
        <Route path="/pricing" element={<PublicLayout><PricingPage /></PublicLayout>} />
        <Route path="/journal" element={<PublicLayout><BlogPage /></PublicLayout>} />
        <Route path="/journal/:slug" element={<PublicLayout><BlogPostPage /></PublicLayout>} />
        <Route path="/contact" element={<PublicLayout><ContactPage /></PublicLayout>} />
        <Route path="*" element={<PublicLayout><Home /></PublicLayout>} />
      </Routes>
    </div>
  );
}

export default function App() {
  return (
    <HelmetProvider>
      <I18nProvider>
        <AuthProvider>
          <BrowserRouter>
            <ScrollToTop />
            <AppShell />
            <Toaster
              theme="dark"
              position="bottom-right"
              toastOptions={{
                style: {
                  background: "#0c0c0e",
                  color: "#f4f3f0",
                  border: "1px solid #222225",
                  borderRadius: 0,
                  fontFamily: "Outfit, sans-serif",
                },
              }}
            />
            <Analytics />
          </BrowserRouter>
        </AuthProvider>
      </I18nProvider>
    </HelmetProvider>
  );
}
